import { API, graphqlOperation } from '../utils/amplifyCompat'
import * as mutations from '../graphql/mutations'
import * as customSubscriptions from '../customgraphql/subscriptions'
import * as customQueries from '../customgraphql/queries'
import { graphqlGetUser } from '../utils/getUserTolerant'
import * as queries from '../graphql/queries'
import * as customMutations from '../customgraphql/mutations'

const REWARD_AVOCADO_ICON = `${process.env.PUBLIC_URL}/images/avocado.svg`
const CHALLENGE_WATERMELON_ICON = `${process.env.PUBLIC_URL}/images/watermelon-icon.svg`

// Create a Notification
// -----------------------------------------------------------------------------
export function createNotificationSubscription() {
  return async (dispatch, getState) => {
    try {
      const subscription = await API.graphql(
        graphqlOperation(customSubscriptions.onCreateNotification)
      ).subscribe({
        next: (eventData) => {
          const notification = eventData?.value?.data?.onCreateNotification
          const currentUserId = getState().user.userData?.id
          // The server also sends the notifications this user created for others; show only
          // the ones they receive. Matched on the scalar FK, so a missing `user` can't
          // silently stop live notifications.
          const notificationUserId =
            notification?.notificationUserId ?? notification?.user?.id

          if (
            getState().user.isAuthenticated &&
            currentUserId &&
            notificationUserId === currentUserId
          ) {
            dispatch({ type: 'CREATE_NOTIFICATION_SUBSCRIPTION', notification })
          }
        },
        error: (err) => {
          console.log('notification subscription connection failed: ', err)
        },
      })

      return () => subscription.unsubscribe()
    } catch (err) {
      console.log('error creating notification subscription: ', err)
      return null
    }
  }
}

// Get More Notifications
// -----------------------------------------------------------------------------
export function getMoreNotifications(username, nextToken) {
  return async (dispatch) => {
    try {
      const data = await graphqlGetUser(
        graphqlOperation(customQueries.getUser, {
          id: username,
          nextTokenNotification: nextToken,
        })
      )
      const newNotificationData = {
        items: data.data.getUser.notifications.items,
        nextToken: data.data.getUser.notifications.nextToken,
      }
      dispatch({ type: 'ADD_NOTIFICATION_DATA', newNotificationData })
    } catch (err) {
      console.log('error reading notification: ', err)
    }
  }
}

// Delete Notification
// -----------------------------------------------------------------------------
export function deleteNotification(notifId) {
  return (dispatch) => {
    try {
      API.graphql(
        graphqlOperation(mutations.deleteNotification, {
          input: { id: notifId },
        })
      )
      dispatch({ type: 'DELETE_NOTIFICATION', notifId })
    } catch (err) {
      console.log('error deleting notification: ', err)
    }
  }
}

// Update Notification
// -----------------------------------------------------------------------------
export function readNotification(notifId) {
  return (dispatch) => {
    try {
      API.graphql(
        graphqlOperation(mutations.updateNotification, {
          input: { id: notifId, read: true },
        })
      )
      dispatch({ type: 'UPDATE_NOTIFICATION', notifId })
    } catch (err) {
      console.log('error reading notification: ', err)
    }
  }
}

// Create Notification when add comment
// -----------------------------------------------------------------------------
export async function createNotificationFromComment(
  commentInput,
  journalAuthorId,
  userData
) {
  if (commentInput.commentAuthorId === journalAuthorId) return
  // The new comment's own ID (the server checks it is the caller's comment on this journal).
  // Without it, fall back to the journal's last loaded comment, as before.
  let commentId = commentInput.id
  if (!commentId) {
    const data = await API.graphql(
      graphqlOperation(customQueries.getPublicJournal, {
        id: commentInput.commentJournalId,
      })
    )
    const comments = data.data.getPublicJournal.comments.items
    commentId = comments[comments.length - 1]?.id
  }
  const notification = {
    content: `added a new comment on your journal`,
    category: 'comment',
    link: `/public-journal/${commentInput.commentJournalId}#${commentId}`,
    read: false,
    actionUser: {
      userID: userData.id,
      displayName: userData.displayName,
      photoURL: userData.profileImage,
    },
    notificationUserId: journalAuthorId,
  }
  const created = await API.graphql(
    graphqlOperation(customMutations.createNotification, { input: notification })
  )
  sendNotification(created.data.createNotification.id)

}

// Create Notification when user loves journal
// -----------------------------------------------------------------------------
export async function createNotificationFromLovedJournal(
  userLovedJournalId,
  journalAuthorId,
  userData
) {
  if (userData.id === journalAuthorId) return
  const notification = {
    content: `liked your journal`,
    category: 'love journal',
    link: `/public-journal/${userLovedJournalId}`,
    read: false,
    actionUser: {
      userID: userData.id,
      displayName: userData.displayName,
      photoURL: userData.profileImage,
    },
    notificationUserId: journalAuthorId,
  }
  const exist = await checkExistNotification(notification, userData.id, false)
  if (exist) return
  const created = await API.graphql(
    graphqlOperation(customMutations.createNotification, { input: notification })
  )
  sendNotification(created.data.createNotification.id)
}

// Create Notification when user loves comment
// -----------------------------------------------------------------------------
export async function createNotificationFromLovedComment(
  comment,
  journalID,
  userData
) {
  if (comment.author.id === userData.id) return
  const notification = {
    content: `liked your comment`,
    category: 'love comment',
    link: `/public-journal/${journalID}#${comment.id}`,
    read: false,
    actionUser: {
      userID: userData.id,
      displayName: userData.displayName,
      photoURL: userData.profileImage,
    },
    notificationUserId: comment.author.id,
  }
  const exist = await checkExistNotification(notification, userData.id, false)
  if (exist) return
  const created = await API.graphql(
    graphqlOperation(customMutations.createNotification, { input: notification })
  )
  sendNotification(created.data.createNotification.id)
}

// Create Notification when user follows author
// -----------------------------------------------------------------------------
export async function createNotificationFromFollow(targetId, userData) {
  if (targetId === userData.id) return
  const notification = {
    content: `is following you`,
    category: 'follow',
    link: `/author/${encodeURIComponent(userData.id)}`,
    read: false,
    actionUser: {
      userID: userData.id,
      displayName: userData.displayName,
      photoURL: userData.profileImage,
    },
    notificationUserId: targetId,
  }
  const exist = await checkExistNotification(notification, targetId, true)
  if (exist) return
  const created = await API.graphql(
    graphqlOperation(customMutations.createNotification, { input: notification })
  )
  sendNotification(created.data.createNotification.id)

}

// Create Notification form 30 journal Avocado
// -----------------------------------------------------------------------------
export async function createNotificationThirtyJournal(event, userId) {
  const notification = {
    content: `You earned an avocado`,
    category: 'reward',
    link: `/author/${userId}`,
    read: false,
    actionUser: {
      userID: 'goodnightjournal',
      displayName: `You wrote ${event} journals!`,
      photoURL: REWARD_AVOCADO_ICON,
    },
    notificationUserId: userId,
  }
  const created = await API.graphql(
    graphqlOperation(customMutations.createNotification, { input: notification })
  )
  sendNotification(created.data.createNotification.id)
}

// Weekly challenge completion notification
// TODO: implement this on when journal is published.
// -----------------------------------------------------------------------------
export async function createNotificationWeeklyChallengeCompletion(
  event,
  userId
) {
  const notification = {
    content: `You completed weekly challenge`,
    category: 'challenge',
    link: `/challenge`,
    read: false,
    actionUser: {
      userID: 'goodnightjournal',
      displayName: `Week of ${event}!`,
      photoURL: CHALLENGE_WATERMELON_ICON,
    },
    notificationUserId: userId,
  }
  const created = await API.graphql(
    graphqlOperation(customMutations.createNotification, { input: notification })
  )
  sendNotification(created.data.createNotification.id)
}

// Send notification on creating chat room
// -----------------------------------------------------------------------------
export async function createChatRoomCreatedNotification(
  chatRoomUserUserId,
  chatRoomUserChatRoomId,
  userData
) {
  const notification = {
    content: `invited you to a chat`,
    category: 'chat',
    link: `/chat/${chatRoomUserChatRoomId}`,
    read: false,
    actionUser: {
      userID: userData.id,
      displayName: userData.displayName,
      photoURL: userData.profileImage,
    },
    notificationUserId: chatRoomUserUserId,
  }

  const created = await API.graphql(
    graphqlOperation(customMutations.createNotification, { input: notification })
  )
  sendNotification(created.data.createNotification.id)
}

// TODO: this check existing notification function should be improved as listNotification won't get exact data that needs to be checked.
// Since sprint PR 7 listNotifications returns only the caller's own notifications (received or
// created), so this sees the ones the caller created before.
async function checkExistNotification(notification, userID, isFollow) {
  const checkData = await API.graphql(
    graphqlOperation(queries.listNotifications, {
      filter: {
        content: { eq: notification.content },
        link: { eq: notification.link },
      },
    })
  )
  const notifications = checkData.data.listNotifications.items
  if (notifications.length === 0) return false
  if (isFollow)
    return notifications.some((item) => item?.user?.id === userID)
  return notifications.some((item) => item?.actionUser?.userID === userID)
}

// Email and push for a notification just created: the server checks it and renders fixed
// templates (security sprint PR 10). A refusal or a send failure doesn't affect the notification.
async function sendNotification(notificationId) {
  try {
    await API.graphql(
      graphqlOperation(customMutations.gnjAmplifySendNotification, { notificationId })
    )
  } catch (err) {
    console.log('notification not sent: ', err?.errors?.[0]?.message ?? err)
  }
}
